import { postUrl } from '@/apiCalls/fetch';
import {
    trackCalculatorEstimate,
    trackedServiceCall,
} from '@/apiCalls/uniqueIDTransferHelper';
import {
    EAddressOwnershipType,
    EBorrowerCitizenshipStatus,
    ECondition,
    ECreditHistory,
    EEmploymentStatus,
    ELoanUsage,
    EQuickQuoteFinanceType,
    ESelfAssessedCreditHistory,
    EVehicleAssetType,
} from '@/context/borrower';
import { EDisplays, selectIsNZ, setIsLoading } from '@/context/display';
import { ILender, setAndSortLenders } from '@/context/lender';
import { store } from '@/context/store';
import {
    ELenderSorting,
    EMatchingEngineLenders,
} from '@/partners/matchingEngineEnums';
import { EPartners } from '@/partners/partnerTypes';
import { getCommissionAmount } from '@/utils/getCommissionAmount';
import { getEmbedContext } from '@/utils/getEmbedContext';

import { IQuickQuoteRequest } from './getQuickQuoteTypes';

export async function getQuickQuote(
    customWaterfall?: string[][]
): Promise<void> {
    const url = '/api/quickQuote2Routes/getQuickQuote';

    // ***** Redux State *****
    const state = store.getState();

    // Store
    const isInitialLoaded = state.displayReducer.initialLoad;
    const isLoading = state.displayReducer.isLoading;
    const assetType = state.borrowerReducer.vehicle.assetType;

    if (!isInitialLoaded || isLoading) {
        return;
    }
    const amount = state.borrowerReducer.loan.purchasePrice;
    if (!amount) {
        store.dispatch(setAndSortLenders([]));
        return;
    }
    const loanPurpose = state.borrowerReducer.loan.loanUsage;
    let isToggleApplicationType: 'consumer' | 'commercial' = 'consumer';
    if (loanPurpose === ELoanUsage.BUSINESS) {
        isToggleApplicationType = 'commercial';
    }
    const sellerName = state.borrowerReducer.seller.name as EPartners;
    const isNZ = selectIsNZ(state);
    const residencyStatus =
        state.borrowerReducer.personalDetails?.citizenshipStatus ??
        (isNZ
            ? EBorrowerCitizenshipStatus.NEWZETLAND_CITIZEN
            : EBorrowerCitizenshipStatus.AUSTRALIAN_CITIZEN);
    const employmentStatus =
        state.borrowerReducer.employments?.employments[0]?.status ??
        EEmploymentStatus.PERMANENT;
    const financeType = getFinanceType();
    const fixedTerm = state.borrowerReducer.loan.loanTerm;
    // For NZ, we don't use Glass Guide valuation - use purchase price instead
    const glassGuideValuation = isNZ
        ? undefined // Don't send Glass Guide for NZ
        : state.borrowerReducer.vehicle.glassValuation ?? amount;
    const deposit = state.borrowerReducer.loan.deposit ?? 0;
    const buildYear = state.borrowerReducer.vehicle.buildYear;

    let assetBalloon =
        state.borrowerReducer.loan.balloon ??
        store.getState().displayReducer.partner?.loanBalloon ??
        0;
    const ownershipType =
        state.borrowerReducer.address.address[0].ownershipType ??
        EAddressOwnershipType.OWNER;
    const amountPaidInBankAccountMonthly =
        state.borrowerReducer.income.paidAmount ?? 4000;
    const creditHistory = getCreditHistory();
    const creditScore = getCreditScore();
    const tradeInQuote = state.borrowerReducer.loan.tradeIn;

    const commissionAmount = getCommissionAmount();
    const waterfallLenders = customWaterfall ?? getWaterfallLenders();
    const makeSpecificLenders = getMakeSpecificLenders();
    const lenderSorting = getLenderSorting();
    const applicantAge = getApplicantAge();
    const assetCondition = getAssetCondition();
    const dateRegisteredForGST =
        state.borrowerReducer.business?.gst || '02/09/2022';
    const establishedDate =
        state.borrowerReducer.business?.abnStatusEffectiveFrom || '02/09/2022';

    const timeAtAddressMonth = state.borrowerReducer.address.address[0]?.months;
    let timeAtAddressYears = state.borrowerReducer.address.address[0]?.years;

    if (timeAtAddressMonth === undefined && timeAtAddressYears === undefined) {
        timeAtAddressYears = 3;
    }

    const timeAtEmployerMonth =
        state.borrowerReducer.employments.employments[0]?.months;
    let timeAtEmployerYear =
        state.borrowerReducer.employments.employments[0]?.years;

    if (timeAtEmployerMonth === undefined && timeAtEmployerYear === undefined) {
        timeAtEmployerYear = 3;
    }

    const brokerFee = 990;

    const partnerId = store.getState().displayReducer.partner.id;
    const vehicleMake = store.getState().borrowerReducer.vehicle.make;
    let balloonValue = 0;

    const annualKms: number | undefined =
        state.borrowerReducer.vehicle.annualKms;
    const hecsDebt: number | undefined = state.borrowerReducer.income.hecsDebt;
    const loanUsage: ELoanUsage = state.borrowerReducer.loan.loanUsage;
    const isNovatedLease =
        state.borrowerReducer.loan.loanUsage === ELoanUsage.NOVATED;
    const income = state.borrowerReducer.income;
    if (isNovatedLease) {
        const balloonRates: Record<number, number> = {
            1: 65.63,
            2: 56.25,
            3: 46.88,
            4: 37.5,
            5: 28.13,
        };
        assetBalloon = balloonRates[fixedTerm];
    }
    const isHecsDebtDisplayed = ((): boolean => {
        if (!income) return false;

        let annualIncome = 0;
        // Convert paid amount to annual based on frequency
        if (income.paidAmount) {
            switch (income.paidFrequency) {
                case 'WEEKLY':
                    annualIncome =
                        (income.paidAmount + (income.otherIncome || 0)) * 52;
                    break;
                case 'FORTNIGHTLY':
                    annualIncome =
                        (income.paidAmount + (income.otherIncome || 0)) * 26;
                    break;
                case 'MONTHLY':
                    annualIncome =
                        (income.paidAmount + (income.otherIncome || 0)) * 12;
                    break;
                default:
                    annualIncome =
                        income.paidAmount + (income.otherIncome || 0);
            }
        }

        return annualIncome < 65000;
    })();
    // note: Hack for lotus campaign cause metro does balloon weirdly
    if (partnerId === ('LOTUS_CAMPAIGN' as unknown as EPartners)) {
        console.log('LOTUS_CAMPAIGN');
        balloonValue = (assetBalloon * amount) / 100;
    }

    const body: IQuickQuoteRequest = {
        // Attribution: for iframe embeds only the client can see the parent page URL.
        // The API turns this into `onBehalfOf` before calling Bifrost.
        ...getEmbedContext(),
        isToggleApplicationType,
        brokerFee,
        country: isNZ ? 'NZ' : 'AU',
        timeAtAddressMonth,
        timeAtAddressYears,
        timeAtEmployerMonth,
        timeAtEmployerYear,
        dateRegisteredForGST,
        establishedDate,
        applicantAge,
        amount,
        ...(isNovatedLease && annualKms ? { annualKms } : {}),
        ...(isHecsDebtDisplayed && hecsDebt ? { hecsDebt } : {}),
        loanUsage,
        partner: sellerName,
        tradeInQuote,
        residencyStatus,
        employmentStatus,
        assetType,
        financeType,
        fixedTerm,
        deposit,
        buildYear,
        assetBalloon,
        balloonValue,
        ownershipType,
        ...(glassGuideValuation !== undefined ? { glassGuideValuation } : {}),
        amountPaidInBankAccountMonthly,
        vehicleMake,
        waterfallLenders,
        makeSpecificLenders,
        commissionAmount,
        comprehensiveEquifaxScore: creditScore,
        vedaEquifaxScore: creditScore,
        creditHistory,
        lenderSorting,
        assetCondition,
    };
    store.dispatch(setIsLoading(true));
    console.log('body', body.vehicleMake);

    try {
        // Tracked as a service call: latency and failures of the most-fired
        // request in the journey, in the same telemetry as the other lookups.
        const response = await trackedServiceCall('quickQuote', () =>
            postUrl<ILender[]>(url, body)
        );

        // Remove broken lenders
        const lenders = response.filter(
            (lender) => lender.advertisedRateInPercent
        );

        store.dispatch(setAndSortLenders(lenders));

        // Tracking only. While the estimate card is the screen showing, the
        // cheapest of these lenders is the "$X per week" it displays — recorded
        // against the inputs this quote was actually requested with, so an edit
        // made while the request was in flight cannot be paired with its figure.
        recordCalculatorEstimateIfShown(lenders, {
            purchasePrice: amount,
            loanTermYears: fixedTerm,
            loanUsage,
        });
    } catch (error) {
        // A quote is an estimate, not a step the journey depends on. Left to
        // propagate it became an unhandled rejection — every caller fires this
        // from an effect without awaiting it — which in development puts the
        // error overlay over the screen and in production rejects unhandled.
        //
        // trackedServiceCall has already recorded the failure, so the loss is
        // visible in telemetry rather than swallowed. The stale quote is
        // cleared: showing the previous figure against the new inputs would be
        // worse than showing none.
        console.error('getQuickQuote failed:', error);
        store.dispatch(setAndSortLenders([]));
    } finally {
        // Always reset the loading state, otherwise a failed request leaves
        // RadioGroup options (and any other isLoading-aware controls) stuck in
        // a disabled state across the rest of the app.
        store.dispatch(setIsLoading(false));
    }

    return;
}

function getApplicantAge(): number | undefined {
    const state = store.getState();
    const dob = state.borrowerReducer.personalDetails?.birthdate;
    if (!dob) {
        return undefined;
    }
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();

    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
}

function getFinanceType(): EQuickQuoteFinanceType {
    const state = store.getState();
    const loanUsage = state.borrowerReducer.loan.loanUsage;
    const assetType = state.borrowerReducer.vehicle.assetType;

    if (loanUsage === ELoanUsage.NOVATED) {
        return EQuickQuoteFinanceType.NOVATED;
    }

    if (assetType === EVehicleAssetType.PERSONAL_LOAN) {
        return EQuickQuoteFinanceType.PERSONAL_USE;
    }

    switch (loanUsage) {
        case ELoanUsage.BUSINESS:
            return EQuickQuoteFinanceType.COMMERCIAL_LOW_DOC;
        default:
        case ELoanUsage.PERSONAL:
            return EQuickQuoteFinanceType.CONSUMER_SECURED;
    }
}

function getWaterfallLenders(): EMatchingEngineLenders[][] {
    const state = store.getState();
    const waterfallLenders = state.displayReducer.waterfallLenders;

    const resultLenders: EMatchingEngineLenders[][] = [];
    waterfallLenders.forEach((waterfallLender) => {
        resultLenders.push(waterfallLender.lenders);
    });

    return resultLenders;
}

function getMakeSpecificLenders(): {
    make: string;
    tierLenders: {
        lenders: EMatchingEngineLenders[];
        showLenderDetails: boolean;
    }[];
}[] {
    const state = store.getState();
    const makeSpecificLenders = state.displayReducer.makeSpecificLenders;

    const result = makeSpecificLenders.map((makeItem) => ({
        make: makeItem.make,
        tierLenders: makeItem.tierLenders.map((tierLender) => ({
            lenders: tierLender.lenders,
            showLenderDetails: tierLender.showLenderDetails,
        })),
    }));

    return result;
}

function getCreditHistory(): ECreditHistory | undefined {
    const state = store.getState();
    const selfAssessedCreditHistory =
        state.borrowerReducer.scores.selfAssessedCreditHistory;

    //todo: dynamic by partner
    switch (selfAssessedCreditHistory) {
        case ESelfAssessedCreditHistory.GREAT:
            return ECreditHistory.CLEAR_CREDIT;
        case ESelfAssessedCreditHistory.DECENT:
            return ECreditHistory.CLEAR_CREDIT;
        case ESelfAssessedCreditHistory.POOR:
            return ECreditHistory.PAID_DEFAULTS;
        default:
            return undefined;
    }
}

function getCreditScore(): number | undefined {
    const state = store.getState();
    const selfAssessedCreditHistory =
        state.borrowerReducer.scores.selfAssessedCreditHistory;

    //todo: dynamic by partner
    switch (selfAssessedCreditHistory) {
        case ESelfAssessedCreditHistory.GREAT:
            return 1200;
        case ESelfAssessedCreditHistory.DECENT:
            return 650;
        case ESelfAssessedCreditHistory.POOR:
            return 450;
    }
}

function getLenderSorting(): ELenderSorting {
    const state = store.getState();
    const lenderSorting = state.displayReducer.lenderSorting;

    return lenderSorting;
}

function getAssetCondition(): ECondition {
    // todo
    // If asset condition is known via params, use params.
    // Else use partner
    // Otherwise check year and kilometers
    const state = store.getState();
    const condition = state.borrowerReducer.vehicle.condition;

    return condition ?? ECondition.NEW;
}

// The screens whose estimate card GenericLenderCard renders the cheapest lender on.
const ESTIMATE_CARD_SCREENS: readonly EDisplays[] = [
    EDisplays.QUICK_CALCULATOR,
    EDisplays.CAMPAIGN_LANDING,
];

function recordCalculatorEstimateIfShown(
    lenders: ILender[],
    inputs: { purchasePrice: number; loanTermYears: number; loanUsage: string }
): void {
    const display = store.getState().displayReducer.display;
    if (!ESTIMATE_CARD_SCREENS.includes(display) || lenders.length === 0) {
        return;
    }

    // Same pick as getCheapestLender, over the response rather than the store.
    const cheapest = lenders.reduce((best, lender) =>
        lender.weeklyRepaymentAmount < best.weeklyRepaymentAmount ? lender : best
    );

    trackCalculatorEstimate({
        ...inputs,
        weeklyRepayment: cheapest.weeklyRepaymentAmount,
        advertisedRate: cheapest.advertisedRateInPercent,
        comparisonRate: cheapest.comparisonRateInPercent,
    });
}
